import React, { useEffect, useState, useMemo } from 'react';
import { ShieldCheck, Camera, Info, CheckCircle } from 'lucide-react';
import { UbicacionEditor, LocationData } from '@/modules/professional/components/UbicacionEditor';
import { CustomSelect } from '@/presentation/components/UI/CustomSelect';
import { ProfessionalLayout } from '@/modules/professional/layouts/ProfessionalLayout';
import { ProfessionalProfileService } from '@/core/application/directory/ProfessionalProfileService';
import { PublicDoctorProfile } from '@/core/domain/types/profile.types';
import { useRoleStore } from '@/core/application/state/useRoleStore';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';

// ─── LicField ───────────────────────────────────────────────────────────────
const LicField = ({ label, fieldKey, profile, originalProfile, setProfile, theme, optional = false }: any) => {
  const locked = !!originalProfile[fieldKey];
  return (
    <div style={{ marginBottom: '20px' }}>
      <label htmlFor={fieldKey} style={{ fontSize: '14px', fontWeight: 700, color: theme.text, marginBottom: '8px', display: 'block' }}>{label}</label>
      <input
        id={fieldKey}
        style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '12px 16px', fontSize: '16px', backgroundColor: locked ? '#F1F5F9' : '#FFFFFF', color: locked ? '#94A3B8' : theme.text, width: '100%', cursor: locked ? 'not-allowed' : 'text', opacity: locked ? 0.5 : 1 }}
        value={profile[fieldKey] || ''}
        disabled={locked}
        onChange={(e) => setProfile({ ...profile, [fieldKey]: e.target.value })}
        placeholder="Escribe tu número de licencia"
      />
    </div>
  );
};

// ─── ToggleSwitch ─────────────────────────────────────────────────────────────
const ToggleSwitch = ({ id, label, checked, onChange, theme }: any) => (
  <div style={styles.switchRow}>
    <label htmlFor={id} style={{ ...styles.switchLabel, color: theme.text, cursor: 'pointer' }}>{label}</label>
    <button
      id={id}
      onClick={() => onChange(!checked)}
      style={{ ...styles.toggle, ...(checked ? { backgroundColor: theme.primary } : { backgroundColor: '#CBD5E1' }) }}
      role="switch"
      aria-checked={checked}
      type="button"
    >
      <div style={{ ...styles.toggleKnob, transform: checked ? 'translateX(20px)' : 'translateX(0)' }} />
    </button>
  </div>
);

// ─── LicenseFields ────────────────────────────────────────────────────────────
const LicenseFields = ({ profile, originalProfile, theme, isTitleValid, setProfile }: any) => {
  if (!isTitleValid) {
    return (
      <div style={{ marginBottom: '20px' }}>
        <label htmlFor="disabled-licencia" style={{ fontSize: '14px', fontWeight: 700, color: theme.text, marginBottom: '8px', display: 'block' }}>Licencias</label>
        <input
          id="disabled-licencia"
          style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '12px 16px', fontSize: '16px', backgroundColor: '#F1F5F9', color: '#94A3B8', width: '100%', cursor: 'not-allowed' }}
          value=""
          disabled
          placeholder="Selecciona un Título Principal primero..."
        />
      </div>
    );
  }

  const title = profile.titulo_principal?.toUpperCase() || '';
  const isVeterinario = title === 'VETERINARIO';
  const isPsicologo = title === 'PSICOLOGO';
  const isFonoaudiologo = title === 'FONOAUDIOLOGO';
  const isMedico = title === 'MEDICO';


  return (
    <>
      {isVeterinario && (
        <>
          <LicField label="Licencia CMV (Obligatoria)" fieldKey="licencia_cmv" profile={profile} originalProfile={originalProfile} setProfile={setProfile} theme={theme} />
          <LicField label="Registro RUNSAI (Opcional)" fieldKey="licencia_runsai" profile={profile} originalProfile={originalProfile} setProfile={setProfile} theme={theme} optional />
        </>
      )}
      {isFonoaudiologo && <LicField label="Licencia MPPS o GO (Obligatoria)" fieldKey="licencia_mpps" profile={profile} originalProfile={originalProfile} setProfile={setProfile} theme={theme} />}
      {!isVeterinario && !isFonoaudiologo && !isPsicologo && <LicField label="Licencia MPPS (Obligatoria)" fieldKey="licencia_mpps" profile={profile} originalProfile={originalProfile} setProfile={setProfile} theme={theme} />}
      {isPsicologo && (
        <>
          <LicField label="Matrícula FPV (Obligatoria)" fieldKey="licencia_fpv" profile={profile} originalProfile={originalProfile} setProfile={setProfile} theme={theme} />
          <LicField label="Registro MPPS (Opcional)" fieldKey="licencia_mpps" profile={profile} originalProfile={originalProfile} setProfile={setProfile} theme={theme} optional />
        </>
      )}
      {isMedico && <LicField label="Colegio Médico / FMV (Opcional)" fieldKey="licencia_fmv" profile={profile} originalProfile={originalProfile} setProfile={setProfile} theme={theme} optional />}
    </>
  );
};

// ─── Componente Principal ─────────────────────────────────────────────────────
export function EditProfileScreen() {
  const theme = useRoleStore((s) => s.getTheme());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [profile, setProfile] = useState<Partial<PublicDoctorProfile>>({});
  const [originalProfile, setOriginalProfile] = useState<Partial<PublicDoctorProfile>>({});
  const [especialidades, setEspecialidades] = useState<any[]>([]);
  const [titulosDisponibles, setTitulosDisponibles] = useState<string[]>([]);

  // Toast State
  const [toastMessage, setToastMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);

  const showToast = (message: string) => {
    setToastMessage(message);
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 3500);
  };

  const getInitials = (nombres?: string, apellidos?: string) => {
    const n = nombres ? nombres.charAt(0).toUpperCase() : '';
    const a = apellidos ? apellidos.charAt(0).toUpperCase() : '';
    return (n + a) || '?';
  };

  useEffect(() => {
    loadProfile();
    loadEspecialidades();
  }, []);

  const loadEspecialidades = async () => {
    const data = await ProfessionalProfileService.getEspecialidades();
    setEspecialidades(data);
    const roles = await ProfessionalProfileService.getClinicalRoles();
    setTitulosDisponibles(roles);
  };

  const isTitleValid = useMemo(() => {
    const title = profile.titulo_principal;
    return !!title && title !== 'PROFESIONAL_CLINICO' && titulosDisponibles.includes(title);
  }, [profile.titulo_principal, titulosDisponibles]);

  const isKycVerified = !!profile.verificado_global;

  const filteredEspecialidades = useMemo(() => {
    if (!isTitleValid || !profile.titulo_principal) return [];
    const title = profile.titulo_principal.toUpperCase();
    return especialidades.filter(esp => {
      const cat = esp.nombre_categoria || '';
      const dom = esp.nombre_dominio || '';
      if (title === 'VETERINARIO') return dom === 'Salud Animal';
      if (title === 'PSICOLOGO') return cat === 'Psicología y Psiquiatría' && esp.nombre_especialidad !== 'Psiquiatría';
      if (title === 'ODONTOLOGO') return cat === 'Odontología';
      if (title === 'FONOAUDIOLOGO') return cat === 'Terapias y Rehabilitación';
      if (title === 'MEDICO') {
        if (esp.nombre_especialidad === 'Psiquiatría') return true;
        return dom === 'Salud Humana' && cat !== 'Psicología y Psiquiatría' && cat !== 'Odontología' && cat !== 'Terapias y Rehabilitación';
      }
      return false;
    });
  }, [especialidades, profile.titulo_principal, isTitleValid]);

  const handleTitleChange = (newTitle: string) => {
    setProfile(prev => ({ ...prev, titulo_principal: newTitle, especialidad_id: undefined }));
  };

  const loadProfile = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId) return;
      const data = await ProfessionalProfileService.getPublicProfileById(userId);
      if (data) {
        if (data.titulo_principal === 'PROFESIONAL_CLINICO') {
          (data as any).titulo_principal = undefined;
        } else if (data.titulo_principal) {
          (data as any).titulo_principal = data.titulo_principal.trim().toUpperCase();
        }
        setProfile(data);
        setOriginalProfile(structuredClone(data));
      }
    } catch (error) {
      console.error(error);
      globalThis.alert('No se pudo cargar tu perfil');
    } finally {
      setLoading(false);
    }
  };

  const handlePickImage = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event: any) => {
          const base64 = event.target.result.split(',')[1];
          uploadImage(base64, URL.createObjectURL(file), file.name);
        };
        reader.readAsDataURL(file);
      }
    };
    fileInput.click();
  };

  const uploadImage = async (base64Data: string, localUri: string, filename: string) => {
    setUploadingImage(true);
    const oldAvatar = profile.avatar_url;
    setProfile(prev => ({ ...prev, avatar_url: localUri }));
    try {
      const extension = filename.split('.').pop() || 'jpg';
      const publicUrl = await ProfessionalProfileService.uploadAvatar(base64Data, extension);
      setProfile(prev => ({ ...prev, avatar_url: publicUrl }));
    } catch (error) {
      console.error(error);
      globalThis.alert('No se pudo subir la foto a la nube.');
      setProfile(prev => ({ ...prev, avatar_url: oldAvatar }));
    } finally {
      setUploadingImage(false);
    }
  };

  const validateBeforeSave = (): string | null => {
    if (!isTitleValid) return 'Por favor, selecciona tu Título Principal antes de guardar.';
    const isVeterinario = profile.titulo_principal?.toUpperCase() === 'VETERINARIO';
    const isPsicologo = profile.titulo_principal?.toUpperCase() === 'PSICOLOGO';
    if (isVeterinario && !profile.licencia_cmv?.trim()) return 'La Licencia CMV es obligatoria para veterinarios.';
    if (isPsicologo && !profile.licencia_fpv?.trim()) return 'La matrícula FPV es obligatoria para psicólogos.';
    if (!isVeterinario && !isPsicologo && !profile.licencia_mpps?.trim()) return 'La Licencia MPPS (o GO) es obligatoria para tu profesión.';
    if (!(profile.atiende_virtual || profile.atiende_presencial || profile.atiende_domicilio)) return 'Debes seleccionar al menos una modalidad de atención.';
    if (!profile.estado_id || !profile.ciudad_id) return 'Falta información de tu ubicación (Estado y Ciudad).';
    return null;
  };

  const proceedSave = async (showMapWarning: boolean) => {
    setSaving(true);
    try {
      await ProfessionalProfileService.updateProfile(profile);
      const msg = showMapWarning ? 'Perfil actualizado. Al no tener mapa exacto, podrías perder visibilidad local.' : '¡Perfil actualizado correctamente!';
      showToast(msg);
    } catch (error) {
      console.error(error);
      globalThis.alert('Hubo un problema guardando tu perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    const errorMsg = validateBeforeSave();
    if (errorMsg) { globalThis.alert(errorMsg); return; }
    if (!profile.latitud || !profile.longitud) {
      const continuar = globalThis.confirm("No has seleccionado una ubicación exacta en el mapa. Esto hará que sea más difícil para los pacientes encontrarte.\n\nPresiona 'Aceptar' para continuar sin mapa, o 'Cancelar' para agregar ubicación.");
      if (continuar) proceedSave(true);
    } else {
      proceedSave(false);
    }
  };

  if (loading) {
    return (
      <ProfessionalLayout>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
          <div style={styles.spinner} />
        </div>
      </ProfessionalLayout>
    );
  }

  return (
    <ProfessionalLayout>
      <div style={styles.scrollContainer}>
        <div style={styles.content}>
          <div style={styles.header}>
            <h1 style={{ ...styles.title, color: theme.text }}>Mi Perfil Clínico</h1>
            <p style={{ ...styles.subtitle, color: theme.textSecondary }}>
              Actualiza cómo te ven los pacientes en el Directorio Aura.
            </p>
          </div>

          <div style={{ ...styles.card, backgroundColor: theme.surface }}>
            
            {isKycVerified && (
              <button
                style={styles.kycBadge}
                onClick={() => globalThis.alert("Identidad Protegida\n\nTu nombre, foto, título y licencia profesional están bloqueados por seguridad.\n\nSi necesitas modificarlos por error o actualización, requerirás pasar nuevamente por el proceso de Alta Clínica (KYC).")}
              >
                <div style={styles.kycBadgeLeft}>
                  <ShieldCheck size={18} color="#059669" />
                  <span style={styles.kycBadgeTitle}>Identidad Verificada</span>
                </div>
                <Info size={20} color="#059669" />
              </button>
            )}

            {/* Avatar */}
            <div style={styles.avatarSection}>
              <button onClick={handlePickImage} style={styles.avatarContainer} disabled={uploadingImage}>
                {profile.avatar_url ? (
                  <img src={profile.avatar_url} alt="Avatar" style={styles.avatarImage} />
                ) : (
                  <div style={{ ...styles.avatarPlaceholder, backgroundColor: theme.primary + '20' }}>
                    <span style={{ ...styles.avatarInitials, color: theme.primary }}>
                      {getInitials(profile.nombres_completos, profile.apellidos_completos)}
                    </span>
                  </div>
                )}
                <div style={styles.editIconBadge}>
                  {uploadingImage ? <div style={styles.spinnerSmallWhite} /> : <Camera size={16} color="#FFF" />}
                </div>
              </button>
              <span style={{ ...styles.avatarHint, color: theme.textSecondary }}>Toca para añadir tu foto</span>
            </div>

            {/* Título */}
            <div style={styles.field}>
              <label htmlFor="titulo_principal" style={{ ...styles.label, color: theme.text }}>Título Principal</label>
              <div style={{ opacity: isTitleValid ? 0.5 : 1, pointerEvents: isTitleValid ? 'none' : 'auto' }}>
                <CustomSelect
                  options={titulosDisponibles.map(t => ({ label: t.charAt(0) + t.slice(1).toLowerCase(), value: t }))}
                  selectedValue={profile.titulo_principal ?? null}
                  onValueChange={(val) => handleTitleChange(val as string)}
                  placeholder="Selecciona tu título..."
                  theme={theme}
                  enabled={!originalProfile.titulo_principal}
                />
              </div>
            </div>

            {/* Especialidad */}
            <div style={styles.field}>
              <label htmlFor="especialidad" style={{ ...styles.label, color: theme.text }}>Especialidad</label>
              <div style={{ opacity: isTitleValid ? 1 : 0.5, pointerEvents: isTitleValid ? 'auto' : 'none' }}>
                <CustomSelect
                  options={filteredEspecialidades.map(esp => ({ label: esp.nombre_especialidad, value: esp.especialidad_id }))}
                  selectedValue={profile.especialidad_id ?? null}
                  onValueChange={(val) => setProfile({ ...profile, especialidad_id: Number(val) })}
                  placeholder="Selecciona una especialidad..."
                  theme={theme}
                  enabled={isTitleValid}
                />
              </div>
            </div>

            <LicenseFields
              profile={profile}
              originalProfile={originalProfile}
              theme={theme}
              isTitleValid={isTitleValid}
              setProfile={setProfile}
            />

            {/* Bio */}
            <div style={styles.field}>
              <label htmlFor="resumen_profesional" style={{ ...styles.label, color: theme.text }}>Resumen Profesional (Bio)</label>
              <textarea
                id="resumen_profesional"
                style={{ ...styles.input, ...styles.textArea, color: theme.text }}
                value={profile.resumen_profesional || ''}
                onChange={(e) => setProfile({ ...profile, resumen_profesional: e.target.value })}
                placeholder="Escribe un breve resumen de tu experiencia..."
                rows={4}
              />
            </div>

            {/* Ubicación */}
            <div style={styles.field}>
              <UbicacionEditor
                theme={theme}
                initialData={{
                  estado_id: profile.estado_id || null,
                  ciudad_id: profile.ciudad_id || null,
                  latitud: profile.latitud || null,
                  longitud: profile.longitud || null,
                  direccion_fisica: profile.direccion_consultorio || ''
                }}
                onChange={(data: LocationData) => setProfile({
                  ...profile,
                  estado_id: data.estado_id,
                  ciudad_id: data.ciudad_id,
                  latitud: data.latitud,
                  longitud: data.longitud,
                  direccion_consultorio: data.direccion_fisica
                })}
              />
            </div>

            {/* Datos Bancarios (Pago Móvil) */}
            <h3 style={{ ...styles.sectionTitle, color: theme.text }}>Datos Bancarios (Pago Móvil)</h3>
            <div style={styles.card}>
              <div style={styles.field}>
                <label htmlFor="pm_banco" style={{ ...styles.label, color: theme.text }}>Banco Emisor</label>
                <input
                  id="pm_banco"
                  style={styles.input}
                  placeholder="Ej: Banesco, Mercantil, BDV"
                  value={profile.pm_banco || ''}
                  onChange={(e) => setProfile({ ...profile, pm_banco: e.target.value })}
                />
              </div>

              <div style={styles.field}>
                <label htmlFor="pm_cedula_prefix" style={{ ...styles.label, color: theme.text }}>Cédula o RIF</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    id="pm_cedula_prefix"
                    style={{ ...styles.input, width: '80px', flexShrink: 0 }}
                    value={(profile.pm_cedula && /^[VJE]-/.test(profile.pm_cedula)) ? profile.pm_cedula.charAt(0) : 'V'}
                    onChange={(e) => {
                      const prefix = e.target.value;
                      const number = profile.pm_cedula ? profile.pm_cedula.replace(/^[VJE]-?/, '') : '';
                      setProfile({ ...profile, pm_cedula: `${prefix}-${number}` });
                    }}
                  >
                    <option value="V">V</option>
                    <option value="J">J</option>
                    <option value="E">E</option>
                  </select>
                  <input
                    style={{ ...styles.input, flex: 1 }}
                    placeholder="Ej: 12345678"
                    value={profile.pm_cedula ? profile.pm_cedula.replace(/^[VJE]-?/, '') : ''}
                    onChange={(e) => {
                      const number = e.target.value.replace(/\D/g, '');
                      const prefix = (profile.pm_cedula && /^[VJE]-/.test(profile.pm_cedula)) ? profile.pm_cedula.charAt(0) : 'V';
                      setProfile({ ...profile, pm_cedula: `${prefix}-${number}` });
                    }}
                    maxLength={10}
                  />
                </div>
              </div>

              <div style={styles.field}>
                <label htmlFor="pm_telefono" style={{ ...styles.label, color: theme.text }}>Teléfono</label>
                <input
                  id="pm_telefono"
                  style={styles.input}
                  placeholder="Ej: 04141234567"
                  value={profile.pm_telefono || ''}
                  onChange={(e) => setProfile({ ...profile, pm_telefono: e.target.value.replace(/\D/g, '') })}
                  maxLength={11}
                />
              </div>
            </div>

            {/* Modalidades */}
            <h3 style={{ ...styles.sectionTitle, color: theme.text }}>Modalidades de Atención</h3>
            <div style={styles.switchesContainer}>
              <ToggleSwitch id="ts_virtual" label="Telemedicina (Virtual)" checked={profile.atiende_virtual || false} onChange={(v: boolean) => setProfile({ ...profile, atiende_virtual: v })} theme={theme} />
              <ToggleSwitch id="ts_presencial" label="Presencial" checked={profile.atiende_presencial || false} onChange={(v: boolean) => setProfile({ ...profile, atiende_presencial: v })} theme={theme} />
              <ToggleSwitch id="ts_domicilio" label="A Domicilio" checked={profile.atiende_domicilio || false} onChange={(v: boolean) => setProfile({ ...profile, atiende_domicilio: v })} theme={theme} />
            </div>
          </div>

          <button
            style={{ ...styles.saveButton, backgroundColor: theme.primary, cursor: (saving || uploadingImage) ? 'not-allowed' : 'pointer', opacity: (saving || uploadingImage) ? 0.7 : 1 }}
            onClick={handleSave}
            disabled={saving || uploadingImage}
          >
            {saving ? <div style={styles.spinnerSmallWhite} /> : <span style={styles.saveButtonText}>Guardar Cambios</span>}
          </button>

          <div style={{ height: '100px' }} />
        </div>
      </div>

      {/* Toast */}
      {toastVisible && (
        <div style={styles.toastContainer}>
          <CheckCircle size={20} color="#FFF" style={{ marginRight: '8px' }} />
          <span style={styles.toastText}>{toastMessage}</span>
        </div>
      )}
      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } } * { box-sizing: border-box; } textarea,input { outline: none; }`}</style>
    </ProfessionalLayout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  scrollContainer: { flex: 1, overflowY: 'auto' },
  content: { padding: '24px', maxWidth: '700px', margin: '0 auto' },
  header: { marginBottom: '24px' },
  title: { fontSize: '28px', fontWeight: 800, marginBottom: '4px', margin: '0 0 4px 0' },
  subtitle: { fontSize: '16px', margin: 0 },
  card: { borderRadius: '16px', padding: '24px', border: '1px solid #E2E8F0', marginBottom: '24px' },
  kycBadge: { backgroundColor: '#ECFDF5', borderRadius: '12px', padding: '12px 16px', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', border: '1px solid #D1FAE5', width: '100%', cursor: 'pointer' },
  kycBadgeLeft: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' },
  kycBadgeTitle: { fontSize: '14px', fontWeight: 600, color: '#065F46' },
  avatarSection: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px' },
  avatarContainer: { width: '100px', height: '100px', borderRadius: '50px', marginBottom: '12px', position: 'relative', boxShadow: '0px 4px 8px rgba(0,0,0,0.1)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  avatarImage: { width: '100px', height: '100px', borderRadius: '50px', objectFit: 'cover' },
  avatarPlaceholder: { width: '100px', height: '100px', borderRadius: '50px', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '2px solid #FFF' },
  avatarInitials: { fontSize: '36px', fontWeight: 700 },
  editIconBadge: { position: 'absolute', bottom: 0, right: 0, backgroundColor: '#3B82F6', width: '32px', height: '32px', borderRadius: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '3px solid #FFF' },
  avatarHint: { fontSize: '14px', fontWeight: 500 },
  field: { marginBottom: '20px', display: 'flex', flexDirection: 'column' },
  label: { fontSize: '14px', fontWeight: 700, marginBottom: '8px', display: 'block' },
  input: { border: '1px solid #E2E8F0', borderRadius: '8px', padding: '12px 16px', fontSize: '16px', backgroundColor: '#FFFFFF', width: '100%' },
  textArea: { minHeight: '100px', resize: 'vertical' },
  sectionTitle: { fontSize: '18px', fontWeight: 700, margin: '16px 0' },
  switchesContainer: { backgroundColor: '#FFFFFF', borderRadius: '12px', padding: '16px', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column' },
  switchRow: { display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #F1F5F9' },
  switchLabel: { fontSize: '16px', fontWeight: 500 },
  toggle: { width: '44px', height: '24px', borderRadius: '12px', position: 'relative', border: 'none', cursor: 'pointer', transition: 'background-color 0.2s', padding: 0, flexShrink: 0 },
  toggleKnob: { position: 'absolute', top: '2px', left: '2px', width: '20px', height: '20px', borderRadius: '10px', backgroundColor: '#FFFFFF', transition: 'transform 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' },
  saveButton: { width: '100%', padding: '16px', borderRadius: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', border: 'none', boxShadow: '0px 2px 4px rgba(0,0,0,0.1)' },
  saveButtonText: { color: '#FFFFFF', fontSize: '18px', fontWeight: 700 },
  toastContainer: { position: 'fixed', bottom: '40px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#1F2937', padding: '12px 20px', borderRadius: '24px', display: 'flex', flexDirection: 'row', alignItems: 'center', boxShadow: '0px 4px 10px rgba(0,0,0,0.2)', zIndex: 9999, animation: 'fadeIn 0.3s ease' },
  toastText: { color: '#FFF', fontSize: '14px', fontWeight: 600 },
  spinner: { width: '40px', height: '40px', border: '4px solid #3B82F6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  spinnerSmallWhite: { width: '24px', height: '24px', border: '3px solid #FFFFFF', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' },
};
